import React, { Suspense, lazy, useState } from "react";
import ErrorBoundary from "./components/boundaries/ErrorBoundary";
import "./App.css";

import LoadingState from "./components/common/LoadingState";
import StatusBanner from "./components/common/StatusBanner";
import AppHeader from "./components/layout/AppHeader";
import DatasetMetaPanel from "./components/layout/DatasetMetaPanel";

import { useMarketplaceDataset } from "./hooks/useMarketplaceDataset";
import { DATASET_PATH } from "./config/appConfig";
import { LuChartPie } from "react-icons/lu";

/* ================= KPI OVERVIEW ================= */
const KpiOverview = lazy(() => import("./components/overview/KpiOverview"));

/* ================= SNAPSHOT ================= */
const InsightsSnapshot = lazy(() => import("./charts/InsightsSnapshot"));

/* ================= TRUST & ADOPTION ================= */
const VerifiedActionsChart = lazy(() =>
  import("./charts/VerifiedActionsChart")
);
const VerifiedActionsVsStarsChart = lazy(() =>
  import("./charts/VerifiedActionsVsStarsChart")
);

/* ================= ECOSYSTEM ================= */
const MostCommonTagsChart = lazy(() => import("./charts/MostCommonTagsChart"));
const VerificationByTagChart = lazy(() =>
  import("./charts/VerificationByTagChart")
);

/* ================= MAINTENANCE HEALTH ================= */
const PopularityVsMaintenanceLoadChart = lazy(() =>
  import("./charts/PopularityVsUnresolvedPainChart")
);
const VerificationImpactChart = lazy(() =>
  import("./charts/VerificationImpactChart")
);

const IssueBacklogRatioChart = lazy(() =>
  import("./charts/IssueBacklogRatioChart")
);

/* ================= MAINTAINER LOAD ================= */
const MaintainerStressIndexChart = lazy(() =>
  import("./charts/MaintainerStressIndexChart")
);
const MaintenanceLoadByTagChart = lazy(() =>
  import("./charts/MaintenanceLoadByTagChart")
);
const ContributorAdequacyChart = lazy(() =>
  import("./charts/ContributorAdequacyChart")
);

/* ================= CATEGORY INSIGHTS ================= */
const TagPopularityVsDifficultyChart = lazy(() =>
  import("./charts/TagPopularityVsDifficultyChart")
);

export default function App() {
  const [showFields, setShowFields] = useState(false);

  const { rows, metadata, status, error, hasData, refresh } =
    useMarketplaceDataset(DATASET_PATH);

  /* ================= INITIAL STATES ================= */
  if (status === "loading" && !hasData) {
    return (
      <LoadingState
        label="Loading dataset"
        description="Fetching GitHub Actions marketplace data…"
      />
    );
  }

  if (status === "error" && !hasData) {
    return (
      <StatusBanner
        variant="error"
        title="Dataset load failed"
        message={error ? String(error) : "Unknown error"}
        actionLabel="Retry"
        onAction={refresh}
      />
    );
  }

  /* ================= MAIN APP ================= */
  return (
    <div className="app-container">
      {/* ================= HEADER ================= */}
      <AppHeader
        metadata={metadata}
        showColumns={showFields}
        onToggleColumns={() => setShowFields((p) => !p)}
        isLoadingMetadata={status === "loading"}
        onRefresh={refresh}
        isRefreshing={status === "loading"}
      />

      {/* ================= DATASET META ================= */}
      <DatasetMetaPanel metadata={metadata} visible={showFields} />

      {/* ================= WARNING ================= */}
      {error && hasData && (
        <StatusBanner
          variant="warning"
          title="Showing cached data"
          message="Latest refresh failed. Using last successful dataset."
          actionLabel="Retry"
          onAction={refresh}
        />
      )}

      {/* ================= CONTENT ================= */}
      <ErrorBoundary>
        <Suspense
          fallback={
            <LoadingState
              label="Building dashboard"
              description="Preparing insights and charts…"
            />
          }
        >
          {/* ================= SNAPSHOT ================= */}

          {/* ================= KPI OVERVIEW ================= */}
          <div className="row">
            <KpiOverview rows={rows} />
          </div>

          {/* ================= TRUST & ADOPTION ================= */}
          <h2 className="section-heading">Trust & Adoption</h2>
          <div className="row card">
            <VerifiedActionsChart rows={rows} />
          </div>
          <div className="row card">
            <VerifiedActionsVsStarsChart rows={rows} />
          </div>

          {/* ================= ECOSYSTEM COMPOSITION ================= */}
          <h2 className="section-heading">Ecosystem Overview</h2>
          <div className="row card">
            <MostCommonTagsChart rows={rows} />
          </div>
          <div className="row card">
            <VerificationByTagChart rows={rows} />
          </div>

          {/* ================= MAINTENANCE HEALTH ================= */}
          <h2 className="section-heading">Maintenance Health</h2>
          <div className="row card">
            <PopularityVsMaintenanceLoadChart rows={rows} />
          </div>
          <div className="row card">
            <VerificationImpactChart rows={rows} />
          </div>
          <div className="row card">
            <IssueBacklogRatioChart rows={rows} />
          </div>

          {/* ================= MAINTAINER LOAD ================= */}
          <h2 className="section-heading">Maintainer Load & Risk</h2>
          <div className="row card">
            <MaintainerStressIndexChart rows={rows} />
          </div>
          <div className="row card">
            <MaintenanceLoadByTagChart rows={rows} />
          </div>
          <div className="row card">
            <ContributorAdequacyChart rows={rows} />
          </div>

          {/* ================= CATEGORY DIFFICULTY ================= */}
          <h2 className="section-heading">Popularity vs Difficulty</h2>
          <div className="row card">
            <TagPopularityVsDifficultyChart rows={rows} />
          </div>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}
